<template>
  <div v-if="hasInvoice || events.length > 0" class="card">
    <div class="card-header bg-white py-1 d-flex align-items-center gap-2">
      <small class="fw-bold">Invoice history</small>
      <small class="text-muted">{{
        transaction.buyer_sales_invoice_number
      }}</small>
      <small
        v-if="transaction.buyer_sales_invoice_due_at"
        class="text-muted ms-auto"
      >
        Due {{ formatDateTimeIntoDate(transaction.buyer_sales_invoice_due_at) }}
        <span
          v-if="transaction.buyer_sales_invoice_paid_at"
          class="badge bg-success ms-1"
          >Paid</span
        >
        <span
          v-else-if="transaction.buyer_sales_invoice_is_overdue"
          class="badge bg-danger ms-1"
          >Overdue</span
        >
      </small>
    </div>
    <div class="card-body p-0">
      <div v-if="isLoading" class="text-center py-3">
        <div
          class="spinner-border spinner-border-sm text-primary"
          role="status"
        >
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
      <div v-else-if="hasError" class="alert alert-warning mb-0 rounded-0">
        Failed to load invoice history.
      </div>
      <div v-else-if="events.length === 0" class="text-muted small px-2 py-2">
        No invoice events recorded yet.
      </div>
      <table v-else class="table table-sm mb-0">
        <thead>
          <tr class="small text-muted">
            <th class="px-2 py-1 fw-normal">When</th>
            <th class="px-2 py-1 fw-normal">Event</th>
            <th class="px-2 py-1 fw-normal">By</th>
            <th v-if="showInvoiceColumn" class="px-2 py-1 fw-normal">
              Invoice
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(event, index) in events" :key="index">
            <td class="px-2 py-1 text-nowrap">
              {{ formatDateTime(event.occurred_at) }}
            </td>
            <td class="px-2 py-1">
              {{ event.description }}
              <span
                v-if="event.event === 'buyer_sales_invoice_canceled'"
                class="badge bg-secondary ms-1"
                >Canceled</span
              >
              <span
                v-else-if="event.event === 'fee_invoice_paid'"
                class="badge bg-success ms-1"
                >Paid</span
              >
            </td>
            <td class="px-2 py-1 text-nowrap">{{ event.actor }}</td>
            <td
              v-if="showInvoiceColumn"
              class="px-2 py-1 text-nowrap text-muted"
            >
              {{ event.invoice_number ?? "" }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  formatDateTime,
  formatDateTimeIntoDate,
} from "@/composables/formatUtils";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import {
  BuyerInvoiceHistoryEvent,
  BuyerInvoiceHistoryResponse,
  SalesTransaction,
} from "@/types";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";

const { globalErrorHandler } = useGlobalHandlers();

const props = defineProps<{
  transaction: SalesTransaction;
}>();

const isLoading = ref(true);
const hasError = ref(false);
const events = ref<BuyerInvoiceHistoryEvent[]>([]);
let unmounted = false;

const hasInvoice = computed(
  () => props.transaction.buyer_sales_invoice_id !== null
);

/** Only worth a column when the trail spans more than one (re-issued) invoice. */
const showInvoiceColumn = computed(
  () => new Set(events.value.map((e) => e.invoice_id)).size > 1
);

/**
 * The history is fetched for every expanded transaction: a transaction whose buyer invoice was
 * canceled has no active invoice but still has a trail worth showing. The card stays hidden only
 * when there is neither an invoice nor any event.
 */
const fetchHistory = async () => {
  isLoading.value = true;
  hasError.value = false;
  try {
    const response = await goSolidApi.get<BuyerInvoiceHistoryResponse>(
      `/api/admin/sales-transactions/${props.transaction.id}/invoice-history`
    );
    if (!unmounted) {
      events.value = response.data.events;
    }
  } catch (error) {
    if (!unmounted) {
      globalErrorHandler(error);
      hasError.value = true;
    }
  } finally {
    isLoading.value = false;
  }
};

onMounted(fetchHistory);
onBeforeUnmount(() => {
  unmounted = true;
});
</script>
